import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquareHeart, Reply, Check } from "lucide-react";

function FeedbackCard({ m, onUpdate }) {
  const [replying, setReplying] = useState(false);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const who = m.member_name || m.name || "Anonymous";
  const contact = m.member_email || m.email;

  const markRead = async () => {
    try { await api.adminFeedbackRead(m.feedback_id); onUpdate(m.feedback_id, { read: true }); }
    catch { toast.error("Couldn't mark as read."); }
  };
  const sendReply = async () => {
    if (!reply.trim()) return toast.error("Write a reply first.");
    setBusy(true);
    try {
      const res = await api.adminFeedbackReply(m.feedback_id, reply.trim());
      toast.success(res.texted ? "Reply delivered to their Welp inbox + texted to their phone." : "Reply delivered to their Welp inbox.");
      onUpdate(m.feedback_id, { read: true, replied: true });
      setReplying(false); setReply("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the reply.");
    } finally { setBusy(false); }
  };

  return (
    <li className={`rounded-2xl border p-5 ${m.read ? "border-black/10 bg-white/70 backdrop-blur-lg" : "border-[#10B981]/40 bg-[#10B981]/5"}`} data-testid={`feedback-msg-${m.feedback_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-lg font-bold text-[#1A1A1A]">{who}</div>
          <div className="text-lg text-[#5A5A5A] mt-0.5">{contact ? `${contact} · ` : ""}{new Date(m.created_at).toLocaleString()}</div>
        </div>
        <div className="flex gap-2 items-center">
          {m.replied && <span className="text-lg px-2.5 py-1 rounded-full bg-[#FF1A82]/15 text-[#FF1A82] border border-[#FF1A82]/30">Replied</span>}
          {!m.read && (
            <button onClick={markRead} className="text-lg text-[#FF1A82] hover:underline inline-flex items-center gap-1" data-testid={`feedback-read-${m.feedback_id}`}>
              <Check className="w-4 h-4" /> Mark read
            </button>
          )}
        </div>
      </div>
      <p className="text-lg text-[#3A3A3A] mt-3 whitespace-pre-line">{m.message}</p>
      {!replying ? (
        <button onClick={() => setReplying(true)} className="mt-3 text-lg uppercase tracking-[0.18em] text-[#10B981] font-semibold hover:underline inline-flex items-center gap-1" data-testid={`feedback-reply-btn-${m.feedback_id}`}>
          <Reply className="w-4 h-4" /> Reply
        </button>
      ) : (
        <div className="mt-3 space-y-2">
          <textarea value={reply} onChange={(e) => setReply(e.target.value)} maxLength={5000} rows={3}
            placeholder={`Reply to ${who.split(" ")[0]}…`}
            className="w-full rounded-xl border border-black/15 px-4 py-3 text-lg bg-white/70 backdrop-blur-lg text-[#1A1A1A] focus:outline-none focus:border-[#10B981] resize-y"
            data-testid={`feedback-reply-text-${m.feedback_id}`} />
          <div className="flex gap-2">
            <button onClick={sendReply} disabled={busy}
              className="px-5 py-2 rounded-full bg-[#10B981] text-white text-lg font-bold hover:brightness-110 disabled:opacity-60"
              data-testid={`feedback-reply-send-${m.feedback_id}`}>{busy ? "Sending…" : "Send reply"}</button>
            <button onClick={() => setReplying(false)} className="px-4 py-2 rounded-full text-lg text-[#5A5A5A] hover:text-[#1A1A1A]">Cancel</button>
          </div>
        </div>
      )}
    </li>
  );
}

export const AdminFeedbackInbox = () => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.adminFeedback().then(d => setItems(d.feedback || [])).catch(() => setItems([]));
  }, []);

  const onUpdate = (id, patch) => setItems(list => list.map(x => x.feedback_id === id ? { ...x, ...patch } : x));
  const unread = (items || []).filter(m => !m.read).length;

  return (
    <div className="mt-6 rounded-2xl border border-black/10 bg-white/70 backdrop-blur-lg shadow-sm p-6" data-testid="admin-feedback-inbox">
      <div className="flex items-center gap-2 text-[#1A1A1A]">
        <MessageSquareHeart className="w-4 h-4 text-[#10B981]" />
        <h2 className="font-heading text-2xl">Feedback inbox</h2>
        {unread > 0 && <span className="text-lg px-2.5 py-1 rounded-full bg-[#10B981] text-white font-bold" data-testid="feedback-unread-count">{unread} new</span>}
      </div>
      <p className="text-lg text-[#5A5A5A] mt-1">Reply in one click — it lands in their Welp inbox and texts their verified phone.</p>
      {items === null ? (
        <p className="text-[#5A5A5A] text-lg mt-4">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-[#5A5A5A] text-lg mt-4">No feedback yet. Members send it from the /feedback page.</p>
      ) : (
        <ul className="mt-5 space-y-3 max-h-[480px] overflow-y-auto pr-1">
          {items.map(m => <FeedbackCard key={m.feedback_id} m={m} onUpdate={onUpdate} />)}
        </ul>
      )}
    </div>
  );
};
